import React, { useState, useEffect } from 'react';
import { amplifyApi } from '../../services/amplifyApi';
import { Link2, MousePointerClick, ShoppingCart, TrendingUp, Copy, Check, Loader2, RefreshCw, AlertCircle, ExternalLink } from 'lucide-react';

function ReferralsTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState({
    totalLinks: 0,
    totalClicks: 0,
    totalConversions: 0,
    conversionRate: 0
  });
  const [copiedCode, setCopiedCode] = useState(null);

  useEffect(() => {
    loadProxyLinks();
  }, []);

  const loadProxyLinks = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await amplifyApi.getMyProxyLinks();

      if (response.success !== false) {
        setLinks(response.links || []);
        setStats({
          totalLinks: response.totalLinks || 0,
          totalClicks: response.totalClicks || 0,
          totalConversions: response.totalConversions || 0,
          conversionRate: response.conversionRate || 0
        });
      } else {
        setError(response.error || 'Failed to load referral links');
      }
    } catch (err) {
      setError(err.message || 'Failed to load referral links');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (code) => {
    try {
      // Always use frontend origin, not API URL from backend
      const fullUrl = `${window.location.origin}/p/${code}`;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600">Loading referral links...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <button
          onClick={loadProxyLinks}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw size={16} />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Links */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <Link2 size={16} />
            <span className="text-sm">Total Links</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats.totalLinks}</p>
        </div>

        {/* Total Clicks */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <MousePointerClick size={16} />
            <span className="text-sm">Total Clicks</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">{stats.totalClicks}</p>
        </div>

        {/* Conversions */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <ShoppingCart size={16} />
            <span className="text-sm">Conversions</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.totalConversions}</p>
        </div>

        {/* Conversion Rate */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 text-slate-500 mb-1">
            <TrendingUp size={16} />
            <span className="text-sm">Conversion Rate</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">
            {Number(stats.conversionRate || 0).toFixed(1)}%
          </p>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <h4 className="font-semibold text-purple-900 mb-2">How Referral Links Work</h4>
        <ul className="text-sm text-purple-700 space-y-1">
          <li>1. Share any product using the Share button in the gallery</li>
          <li>2. When someone clicks your link and makes a purchase, you earn 5% commission</li>
          <li>3. Links track clicks for 30 days after the first click</li>
        </ul>
      </div>

      {/* Referral Links Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Your Referral Links</h3>
          <button
            onClick={loadProxyLinks}
            className="text-slate-500 hover:text-slate-700 p-1"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>

        {links.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500">
            <Link2 size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No referral links yet</p>
            <p className="text-sm mt-1">
              Click the Share button on any product in the gallery to create a referral link.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Created</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-600">Link Code</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Clicks</th>
                  <th className="text-center px-4 py-3 font-medium text-slate-600">Conversions</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {links.map((link, index) => (
                  <tr key={link.proxyCode || index} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">
                      {formatDate(link.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-slate-100 rounded text-xs font-mono">
                          /p/{link.proxyCode}
                        </code>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-medium text-blue-600">
                        {link.clickCount || link.clicks || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-medium text-green-600">
                        {link.conversionCount || link.conversions || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => copyToClipboard(link.proxyCode)}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            copiedCode === link.proxyCode
                              ? 'bg-green-100 text-green-700'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {copiedCode === link.proxyCode ? (
                            <>
                              <Check size={14} />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy size={14} />
                              Copy
                            </>
                          )}
                        </button>
                        <a
                          href={`/p/${link.proxyCode}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg text-xs font-medium"
                        >
                          <ExternalLink size={14} />
                          Open
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="bg-slate-100 rounded-xl p-4 text-sm text-slate-600">
        <p>
          <strong>Tip:</strong> Share your links on social media, in messages, or embed them in blog posts.
          The more targeted your audience, the higher your conversion rate!
        </p>
      </div>
    </div>
  );
}

export default ReferralsTab;
